// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac - Methodology Performance Component
// Comparison table of methodology performance (Sandler vs Challenger vs SPIN)
// ═══════════════════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { SkeletonCard } from '@/components/ui/skeleton-card';
import { cn } from '@/lib/utils';
import { Trophy, TrendingUp, TrendingDown } from 'lucide-react';
import { formatUiNumber, formatUiPercent } from '@/lib/ui-format';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface MethodologyPerformanceData {
  methodology: string;
  draftsGenerated: number;
  approvalRate: number;
  responseRate: number;
  avgAiConfidence: number;
  actualSuccessRate: number;
  calibrationError: number;
  avgResponseTimeHours: number;
  meetingsBooked: number;
}

interface MethodologyPerformanceProps {
  data?: MethodologyPerformanceData[];
  isLoading?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const getCalibrationErrorColor = (error: number): string => {
  if (error < 10) {
    return 'bg-status-success/10 text-status-success border-status-success/30';
  }
  if (error <= 20) {
    return 'bg-status-warning/10 text-status-warning border-status-warning/30';
  }
  return 'bg-status-danger/10 text-status-danger border-status-danger/30';
};

const formatPercentage = (value: number): string => {
  return formatUiPercent(value / 100, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

const formatNumber = (value: number): string => {
  return formatUiNumber(value);
};

const formatHours = (hours: number): string => {
  if (hours < 24) {
    return `${hours.toFixed(1)}h`;
  }
  return `${(hours / 24).toFixed(1)}d`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const MethodologyPerformance = ({
  data,
  isLoading = false,
}: MethodologyPerformanceProps): JSX.Element => {
  // Sort methodologies by response rate (best first)
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => b.responseRate - a.responseRate);
  }, [data]);

  // Find best-performing methodology (highest response rate)
  const bestMethodology = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return null;
    return sortedData[0]?.methodology ?? null;
  }, [sortedData]);

  // Loading state
  if (isLoading) {
    return <SkeletonCard className="h-96" />;
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Methodology Performance</CardTitle>
          <CardDescription>
            Response Rate = % of sent messages that got replies
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No methodology performance data yet
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Methodology Performance</CardTitle>
        <CardDescription>
          Response Rate = % of sent messages that got replies. Comparing AI Confidence vs Actual Performance.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table aria-label="Methodology performance">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Methodology</TableHead>
                <TableHead className="text-right">Drafts</TableHead>
                <TableHead className="text-right">Approval %</TableHead>
                <TableHead className="text-right">Response %</TableHead>
                <TableHead className="text-right">AI Confidence</TableHead>
                <TableHead className="text-right">Actual Performance</TableHead>
                <TableHead className="text-right">Calibration Error</TableHead>
                <TableHead className="text-right">Avg Response Time</TableHead>
                <TableHead className="text-right">Meetings</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((row) => {
                const isBest = row.methodology === bestMethodology;
                const confidenceDiff = row.actualSuccessRate - row.avgAiConfidence;
                const ConfidenceIcon = confidenceDiff > 0 ? TrendingUp : TrendingDown;
                const confidenceColor = confidenceDiff > 0 ? 'text-status-success' : 'text-status-danger';

                return (
                  <TableRow
                    key={row.methodology}
                    className={cn(
                      isBest && 'bg-primary/5 border-l-4 border-l-primary'
                    )}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {isBest && (
                          <Trophy className="w-4 h-4 text-primary" />
                        )}
                        <span>{row.methodology}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(row.draftsGenerated)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatPercentage(row.approvalRate)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn(
                        'font-semibold',
                        isBest && 'text-primary'
                      )}>
                        {formatPercentage(row.responseRate)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <span>{formatPercentage(row.avgAiConfidence)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <span>{formatPercentage(row.actualSuccessRate)}</span>
                        {Math.abs(confidenceDiff) > 1 && (
                          <ConfidenceIcon className={cn('w-3 h-3', confidenceColor)} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant="outline"
                        className={cn(
                          'font-mono text-xs',
                          getCalibrationErrorColor(Math.abs(row.calibrationError))
                        )}
                      >
                        {formatPercentage(Math.abs(row.calibrationError))}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatHours(row.avgResponseTimeHours)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(row.meetingsBooked)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
